import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Lightbulb, AlertTriangle, Info, CheckCircle2 } from "lucide-react";

interface Recommendation {
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  category: string;
}

interface RecommendationPanelProps {
  recommendations: Recommendation[];
}

const priorityConfig = {
  high: {
    icon: AlertTriangle,
    color: "border-l-red-500 bg-red-50/50",
    badgeVariant: "destructive" as const,
    iconColor: "text-red-600"
  },
  medium: {
    icon: Info,
    color: "border-l-amber-500 bg-amber-50/50",
    badgeVariant: "default" as const,
    iconColor: "text-amber-600"
  },
  low: {
    icon: CheckCircle2,
    color: "border-l-blue-500 bg-blue-50/50",
    badgeVariant: "secondary" as const,
    iconColor: "text-blue-600"
  }
};

const translateCategory = (category: string): string => {
  const translations: Record<string, string> = {
    "medication": "Medicação",
    "lab": "Exames Laboratoriais",
    "lifestyle": "Estilo de Vida",
    "followup": "Acompanhamento",
    "specialist": "Especialista"
  };
  return translations[category] || category;
};

export function RecommendationPanel({ recommendations }: RecommendationPanelProps) {
  const sortedRecommendations = [...recommendations].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  return (
    <Card className="shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Lightbulb className="h-5 w-5 text-primary" />
          Recomendações Priorizadas
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {sortedRecommendations.map((rec, index) => {
          const config = priorityConfig[rec.priority];
          const Icon = config.icon;
          
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className={`border-l-4 ${config.color} hover:shadow-md transition-shadow`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg bg-background ${config.iconColor}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-semibold text-sm">{rec.title}</h4>
                        <Badge variant={config.badgeVariant} className="text-xs shrink-0">
                          {rec.priority === "high" ? "Alta" : rec.priority === "medium" ? "Média" : "Baixa"}
                        </Badge>
                      </div>
                      
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {rec.description}
                      </p>
                      
                      <Badge variant="outline" className="text-xs">
                        {translateCategory(rec.category)}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </CardContent>
    </Card>
  );
}
